/**
 * Reusable Input Component
 * ClassroomAI-inspired design
 */
import React, { InputHTMLAttributes, forwardRef } from 'react';

interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: string;
  helperText?: string;
  fullWidth?: boolean;
}

const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ label, error, helperText, fullWidth = false, className = '', ...props }, ref) => {
    const widthClass = fullWidth ? 'w-full' : '';
    const errorClass = error ? 'border-red-400 focus:ring-red-500 focus:border-red-500' : 'border-gray-200 dark:border-secondary-600 focus:ring-primary-500 focus:border-primary-500';

    return (
      <div className={`${widthClass} ${className}`}>
        {label && (
          <label htmlFor={props.id} className="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-1.5">
            {label}
            {props.required && <span className="text-red-500 ml-1">*</span>}
          </label>
        )}
        <input
          ref={ref}
          className={`
            block ${fullWidth ? 'w-full' : ''} px-4 py-3
            border-2 ${errorClass}
            rounded-2xl
            bg-white dark:bg-secondary-700 text-gray-900 dark:text-white
            placeholder-gray-400 dark:placeholder-secondary-400
            focus:outline-none focus:ring-4 focus:ring-primary-500/20 focus:ring-offset-0
            transition-all duration-200
            disabled:bg-gray-100 dark:disabled:bg-secondary-800 disabled:cursor-not-allowed
          `}
          {...props}
        />
        {error && (
          <p className="mt-1.5 text-sm text-red-600">{error}</p>
        )}
        {helperText && !error && (
          <p className="mt-1.5 text-sm text-gray-500">{helperText}</p>
        )}
      </div>
    );
  }
);

Input.displayName = 'Input';

export default Input;
